/**
 * 长图稿 / 长图作品名里的「用途段」(1006 演示机最终回归)。
 *
 * 现象:从「企业介绍长图」模板一键出的作品,在「我的作品」标签是「视觉舱」、名字「一键长图·复测珠宝门店1006:一家社…」。
 * 根因:一键长图的海报任务由 api 服务端建单(`LongImageOneClickOrchestrationService.startGeneration`),
 *   body 不带 sceneKey,作品名取锚壳草稿名(「详情长图·」→「一键长图·」);锚壳出图成功即清,
 *   列表 DTO 也没有用途字段 —— 作品卡**拿不到用途**,标签只能退成线名「视觉舱」。
 * 修法(console 单侧,api 零改动):建锚壳时把用途写进草稿名的第二段 ——
 *   「详情长图·企业介绍长图·{主题}·MM-DD HH:mm」。api 的前缀替换与视频标题剥前缀仍只认第一段
 *   (「详情长图·/一键长图·」),不受影响;显示层按本文件解析:用途段 → 标签,其后 → 名字。
 * 用途白名单与 `longImageUsageQuery` / 草稿卡同一份(`LONG_IMAGE_USAGES`),段名 = 该用途卡标题;
 * 电商详情(goods)是默认用途,不写段(名字保持旧形)。
 */
import { cardOfSceneKey } from "@/entities/scene-card/catalog";
import { LONG_IMAGE_USAGES } from "@/entities/scene-card/model/presets";

const LINE_PREFIXES = ["详情长图", "一键长图"] as const;

/** 用途 → 名字里的用途段(= 用途卡标题);goods / 白名单外 / 查不到卡 → null。 */
export function longImageUsageSegment(usage: string | null | undefined): string | null {
  if (!usage || usage === "goods") return null;
  if (!(LONG_IMAGE_USAGES as readonly string[]).includes(usage)) return null;
  return cardOfSceneKey(`longimage_${usage}`)?.title ?? null;
}

export interface ParsedLongImageName {
  /** 线别前缀(「详情长图」/「一键长图」)。 */
  line: (typeof LINE_PREFIXES)[number];
  /** 解析出的用途(只有名字里带用途段时才有)。 */
  usage: string | null;
  /** 去掉线别前缀与用途段之后的部分(主题·时间)。 */
  rest: string;
}

/** 解析「{线别}·[{用途段}·]{主题}·{时间}」;不是长图线别名 → null。 */
export function parseLongImageName(name: string | null | undefined): ParsedLongImageName | null {
  const n = (name ?? "").trim();
  const line = LINE_PREFIXES.find((p) => n.startsWith(`${p}·`));
  if (!line) return null;
  const after = n.slice(line.length + 1);
  for (const u of LONG_IMAGE_USAGES) {
    const seg = longImageUsageSegment(u);
    if (seg && after.startsWith(`${seg}·`)) {
      return { line, usage: u, rest: after.slice(seg.length + 1) };
    }
  }
  return { line, usage: null, rest: after };
}

/** 名字里带用途段 → 该用途卡的 sceneKey(`longimage_corp` …,作品卡标签据此显示用途卡名);否则 null。 */
export function longImageSceneKeyOfName(name: string | null | undefined): string | null {
  const p = parseLongImageName(name);
  return p?.usage ? `longimage_${p.usage}` : null;
}

/**
 * 作品**显示名**:带用途段的长图名 →「主题·时间」(用途由标签说,不在名字里重复);
 * 其余原样(trim)。作品卡(`mapPoster`)、任务详情页标题、视频创建页「海报「…」」横幅同一口径。
 */
export function longImageDisplayName(name: string | null | undefined): string {
  const n = (name ?? "").trim();
  const p = parseLongImageName(n);
  return p?.usage ? p.rest : n;
}
